import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, VolumeX, SkipBack, SkipForward, Settings2 } from "lucide-react";

// ── Custom hook for speech synthesis ──

function useSpeechSynthesis() {
  const [voices, setVoices] = useState([]);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const utteranceRef = useRef(null);
  const currentTextRef = useRef("");

  useEffect(() => {
    const load = () => {
      const available = window.speechSynthesis.getVoices();
      setVoices(available);
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const speak = useCallback((text, { voice, rate = 1, pitch = 1 } = {}) => {
    if (!text || !text.trim()) return;
    window.speechSynthesis.cancel();
    currentTextRef.current = text;
    const utterance = new SpeechSynthesisUtterance(text);
    if (voice) utterance.voice = voice;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utteranceRef.current = utterance;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => { setSpeaking(false); setPaused(false); };
    utterance.onerror = () => { setSpeaking(false); setPaused(false); };
    window.speechSynthesis.speak(utterance);
  }, []);

  const pause = useCallback(() => {
    if (speaking) {
      window.speechSynthesis.pause();
      setPaused(true);
    }
  }, [speaking]);

  const resume = useCallback(() => {
    if (paused) {
      window.speechSynthesis.resume();
      setPaused(false);
    }
  }, [paused]);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
    currentTextRef.current = "";
  }, []);

  return { voices, speaking, paused, speak, pause, resume, stop };
}

// ── Voice Settings Panel ──

export default function VoiceController({
  enabled, onToggle,
  voiceId, onVoiceChange,
  rate, onRateChange,
  compact = false,
}) {
  const [voices, setLocalVoices] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const load = () => setLocalVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  // Group voices by estimated gender (based on name heuristics)
  const maleVoices = voices.filter(v =>
    /male|man|david|james|john|daniel|paul|mark|donald|george|ken|mike|rich|tom|victor|will|alex|bob|charlie|frank|harry|jack|karl|leo|nick|oliver|peter|quinn|ray|sam|tim|ulf|vince|walter|xavier|yuri|zeke/i.test(v.name)
  );
  const femaleVoices = voices.filter(v =>
    /female|woman|samantha|fiona|karen|susan|linda|mary|patricia|jennifer|barbara|elizabeth|margaret|jessica|sarah|emma|olivia|ava|sophia|isabella|mia|charlotte|amelia|harper|evelyn|abigail|emily|ella|avery|sofia|mia|zoe|ella|lily|hannah|aria|scarlett|grace|chloe|victoria|riley|auburn|aubrey|lena|nora|alice|freya|hazel|ivy|daisy|rose|iris|poppy|holly|jasmine|ruby|ella/i.test(v.name)
  );

  // Fallback: if no gendered voices found, separate by common indicators
  const allVoices = voices.length > 0 ? voices : [];

  const displayRate = rate <= 0.7 ? "Slow" : rate >= 1.8 ? "Fast" : rate <= 1.1 ? "Normal" : "Fast";

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onToggle}
          className={`p-1.5 rounded-md transition-colors ${
            enabled ? "text-violet-600 bg-violet-100" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          }`}
          title={enabled ? "Mute voice" : "Enable voice"}
        >
          {enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        {enabled && (
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100"
            title="Voice settings"
          >
            <Settings2 className="w-3.5 h-3.5" />
          </button>
        )}
        {showSettings && (
          <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-64">
            <VoiceSettings
              voices={voices}
              maleVoices={maleVoices}
              femaleVoices={femaleVoices}
              voiceId={voiceId}
              onVoiceChange={onVoiceChange}
              rate={rate}
              onRateChange={onRateChange}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Volume2 className="w-5 h-5 text-violet-500" />
          <h3 className="font-semibold text-slate-800">Voice Output</h3>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={onToggle}
            className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
          />
          <span className="text-sm text-slate-600">{enabled ? "On" : "Off"}</span>
        </label>
      </div>

      {enabled && (
        <VoiceSettings
          voices={voices}
          maleVoices={maleVoices}
          femaleVoices={femaleVoices}
          voiceId={voiceId}
          onVoiceChange={onVoiceChange}
          rate={rate}
          onRateChange={onRateChange}
        />
      )}
    </div>
  );
}

function VoiceSettings({
  voices, maleVoices, femaleVoices,
  voiceId, onVoiceChange,
  rate, onRateChange,
}) {
  const voiceGroups = [
    { label: "Male Voices", list: maleVoices },
    { label: "Female Voices", list: femaleVoices },
    { label: "Other Voices", list: voices.filter(v =>
      !maleVoices.includes(v) && !femaleVoices.includes(v)
    )},
  ].filter(g => g.list.length > 0);

  return (
    <div className="space-y-3">
      {/* Voice selection */}
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Voice</label>
        <select
          value={voiceId || ""}
          onChange={(e) => onVoiceChange(e.target.value || null)}
          className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-violet-500"
        >
          <option value="">Default voice</option>
          {voiceGroups.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.list.map(v => (
                <option key={v.name} value={v.name}>
                  {v.name.replace(/\s*\(.*?\)\s*/g, "")}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Speed control */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-slate-500">Speed</label>
          <span className="text-xs font-medium text-violet-600">
            {rate.toFixed(1)}x
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-6">0.5x</span>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={rate}
            onChange={(e) => onRateChange(parseFloat(e.target.value))}
            className="flex-1 h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-violet-600"
          />
          <span className="text-[10px] text-slate-400 w-6 text-right">2x</span>
        </div>
      </div>
    </div>
  );
}

export { useSpeechSynthesis };
